import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Link, Navigate, useMatch, useNavigate } from 'react-router-dom';
import { Table, useLocalizeMessage } from 'libs.nucleus';
import { PageHeader, PageWrapper, useBooleanState } from 'libs.react';
import TableCustomHeader from './components/table_custom_header.component';
import { getTableRowCustomClasses } from './helpers/pi_oversight_list.helper';
import { usePIOversightListTableColumns } from './hooks/pi_oversight_list_table_columns.hook';
import { ParticipantFormModal } from '../../components/participant_form/participant_form_modal.component';
import { PiOversightDetailsModal } from '../../components/pi_oversight_details/pi_oversight_details_modal.component';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
import { useLumosAuth } from '../../hooks/use_lumos_auth';
import { usePIOversight } from '../../hooks/use_pi_oversight';
import { usePIOversightAccess } from '../../hooks/use_pi_oversight_access';
import { LUMOS_PERMISSIONS } from '../../types/permissions.types';
export const PIOversightListPage = () => {
    const translate = useLocalizeMessage();
    const { isUserPermitted } = useLumosAuth();
    const { environment } = useCurrentEnvironment();
    const navigate = useNavigate();
    const isPIOversightEnabled = usePIOversightAccess();
    const match = useMatch('/site/study/:studyId/oversight/:participantId?');
    const basePathName = `/site/study/${environment?.id}/oversight`;
    const [isParticipantFormOpen, setParticipantFormOpen, setParticipantFormClose] = useBooleanState();
    const handleOpenPIOversightDetailsModal = (participantId) => {
        navigate(`${basePathName}/${participantId}`);
    };
    const handleClosePIOversightDetailsModal = () => {
        navigate(basePathName);
    };
    const { pagination, piOversightStatus, handlePaginationChange, handleSearchChange, handleSearchClear, handleSelectPIOversightStatus, piOversightData, fetchPIOversightListRequestState, } = usePIOversight();
    const oversightTableColumns = usePIOversightListTableColumns({
        onOpenPIOversightDetailsModal: handleOpenPIOversightDetailsModal,
    });
    const { piOversights, pageCount } = useMemo(() => {
        if (fetchPIOversightListRequestState?.isLoading) {
            return {
                piOversights: [],
                pageCount: 0,
            };
        }
        const { data, totalCount } = piOversightData;
        return {
            piOversights: data,
            pageCount: Math.ceil(totalCount / pagination.pageSize),
        };
    }, [piOversightData, fetchPIOversightListRequestState]);
    const headerOptions = [];
    if (isUserPermitted(LUMOS_PERMISSIONS.PARTICIPANTS_CREATE)) {
        headerOptions.push({
            name: translate('action.participant.create'),
            onClick: setParticipantFormOpen,
            variant: 'primary',
            dataTestId: 'create-participant-button',
        });
    }
    if (!isPIOversightEnabled) {
        return _jsx(Navigate, { to: `/site/study/${environment?.id}/participants` });
    }
    return (_jsxs(PageWrapper, { children: [_jsx(PageHeader, { title: translate('generic.pi_signature'), options: headerOptions, LinkComponent: Link }), _jsx(ParticipantFormModal, { isModalOpen: isParticipantFormOpen, onClose: setParticipantFormClose }), _jsx("div", { id: 'pi-oversight-table-wrapper', children: _jsx(Table, { columns: oversightTableColumns, data: piOversights || [], isLoading: fetchPIOversightListRequestState?.isLoading, getRowId: (row) => row.participantId, labelNoResults: translate('info.pi_oversight_table.empty'), paginationNavigationScreenReaderLabel: translate('info.pi_oversight_table.navigation'), initialPageSize: 10, hasPagination: true, manualFiltering: true, useInternalSearch: true, onSearch: handleSearchChange, onSearchClear: handleSearchClear, labelSearchField: translate('generic.search'), onPaginationChange: handlePaginationChange, manualPagination: true, manualPaginationState: pagination, pageCount: pageCount, getRowCustomClasses: getTableRowCustomClasses, customHeaderContent: _jsx(TableCustomHeader, { piOversightStatus: piOversightStatus, onSelectPIOversightStatus: handleSelectPIOversightStatus }) }) }), _jsx(PiOversightDetailsModal, { isOpen: !!match?.params.participantId, participantId: match?.params.participantId ?? '', onClose: handleClosePIOversightDetailsModal })] }));
};
